// Pure state logic for the notification toggles, kept out of Notifications.js
// so it's testable without component-test infra.

// Mirrors Gitcha-API's NOTIFICATION_SETTINGS_FIELDS defaults - keep in sync.
// On-by-default because the API treats a MISSING settings row as "send", so a
// user who never saved a row is receiving these emails today.
export const TOGGLE_DEFAULTS = {
  Introductions: true,
  AccountActivity: false,
  Messages: true,
  Matches: true,
  MatchRecap: true,
  MlsMatchDigest: true,
  StandoutMatch: true,
  UpgradeNudge: true,
  ListingSyndicated: true,
  ListingActivity: true,
  WeeklyDigest: true,
  Onboarding: true,
};

// A missing/pending/failed row falls back to the same on-by-default state the
// API applies, not the misleading all-off state an empty object would give.
export function buildSettingsState(apiRow) {
  const next = { ...TOGGLE_DEFAULTS };
  if (apiRow) {
    Object.keys(TOGGLE_DEFAULTS).forEach((key) => {
      if (apiRow[key] !== undefined) {
        next[key] = apiRow[key] === 1;
      }
    });
  }
  return next;
}

// Reverts on failure - a failed save must never leave the UI showing a
// preference that was never persisted.
export function applyToggleResult(
  settings,
  key,
  optimisticValue,
  previousValue,
  saveSucceeded
) {
  return {
    ...settings,
    [key]: saveSucceeded ? optimisticValue : previousValue,
  };
}

// AccountActivity is excluded: it's the billing preference and has no
// unsubscribe category on the API side.
export const UNSUBSCRIBE_ELIGIBLE_KEYS = Object.keys(TOGGLE_DEFAULTS).filter(
  (key) => key !== "AccountActivity"
);

// Derived rather than stored, so the master toggle can't drift out of sync
// with the individual ones.
// `keys` lets callers judge only the toggles the user can actually see; a
// hidden toggle left on would otherwise keep the master box unchecked.
export function isUnsubscribedFromAll(
  settings,
  keys = UNSUBSCRIBE_ELIGIBLE_KEYS
) {
  return keys.every((key) => !settings[key]);
}

export function buildAllTogglePayload(value) {
  return UNSUBSCRIBE_ELIGIBLE_KEYS.reduce((acc, key) => {
    acc[key] = value;
    return acc;
  }, {});
}

// Checked state ("I am unsubscribed from all") is the inverse of the toggle
// value to write. Named and tested rather than inverted inline, because a bare
// `!` at the call site had this backwards and turned every category ON.
export function toggleValueForUnsubscribeAll(shouldUnsubscribeAll) {
  return !shouldUnsubscribeAll;
}

// Reverts each key to its own `previousValues` entry, not a blanket true -
// some toggles may already have been off before the attempt.
export function applyAllToggleResult(
  settings,
  previousValues,
  value,
  saveSucceeded
) {
  if (saveSucceeded) {
    return { ...settings, ...buildAllTogglePayload(value) };
  }
  return { ...settings, ...previousValues };
}
